import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 lg:py-20">
      <Helmet>
        <title>Privacy Policy | A Gentle Touch HomeCare - Columbus, Ohio</title>
        <meta name="description" content="Privacy Policy for A Gentle Touch HomeCare. Learn how we protect your personal information and comply with Ohio privacy regulations for home care services." />
        <link rel="canonical" href="https://agentletouchhomecare.com/privacy-policy" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
          {/* Header */}
          <h1 className="text-3xl lg:text-4xl font-bold text-[#1a3c4d] mb-2 text-center">
            Privacy Policy
          </h1>
          <p className="text-xl text-[#20a0a0] font-semibold text-center mb-4">
            A Gentle Touch HomeCare
          </p>
          <p className="text-gray-500 text-center mb-10">
            Effective Date: 9.7.2024
          </p>

          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-8">
              A Gentle Touch HomeCare ("we," "our," or "us") is committed to protecting the privacy and confidentiality of individuals who visit our website and use our non medical home care services. This Privacy Policy explains how we collect, use, protect, and share personal information in compliance with applicable Ohio laws and regulations.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                Information We Collect
              </h2>
              <p className="mb-4">
                We may collect personal information when you contact us, request services, or submit forms on our website. This information may include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Name</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Mailing address</li>
                <li>Information related to service inquiries</li>
              </ul>
              <p>
                We do not knowingly collect information from children under the age of 13.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                How Your Information Is Used
              </h2>
              <p className="mb-4">
                Your information is used only to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Respond to inquiries</li>
                <li>Provide or coordinate non medical home care services</li>
                <li>Communicate with clients and families</li>
                <li>Maintain client records as required by Ohio law</li>
                <li>Improve our website and services</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                Non Medical Services Disclosure (Ohio Compliance)
              </h2>
              <p className="mb-4">
                A Gentle Touch HomeCare is a non medical home care provider. We provide assistance with activities of daily living, companionship, and supportive services.
              </p>
              <p className="mb-4">
                We do not provide medical care, nursing services, therapy, diagnosis, or treatment.
              </p>
              <p>
                Information submitted through this website does not create a medical provider patient relationship.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                Protection of Information
              </h2>
              <p>
                We follow reasonable administrative, physical, and electronic safeguards to protect personal information in compliance with Ohio privacy and record keeping standards for non medical home care agencies.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                Sharing of Information
              </h2>
              <p className="mb-4">
                We do not sell or rent personal information. Information may be shared only:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>With staff or contractors providing services</li>
                <li>With authorized billing or scheduling systems</li>
                <li>When required by Ohio law, court order, or regulatory authority</li>
                <li>To protect the safety of clients or the public</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                Website Cookies
              </h2>
              <p>
                Our website may use cookies or tracking tools to improve functionality and understand visitor traffic. You may disable cookies through your browser settings.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                Your Rights
              </h2>
              <p className="mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Request access to your personal information</li>
                <li>Request corrections to inaccurate data</li>
                <li>Request deletion of information when permitted by law</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4 border-b-2 border-[#20a0a0] pb-2">
                Updates to This Policy
              </h2>
              <p>
                This policy may be updated at any time. Changes will be posted on this page.
              </p>
            </section>

            {/* Contact Section */}
            <section className="bg-[#e8f4f4] rounded-xl p-6 mt-10">
              <h2 className="text-xl font-bold text-[#1a3c4d] mb-4">
                Contact Information
              </h2>
              <p className="mb-4">
                For privacy questions or concerns, please contact:
              </p>
              <p className="font-semibold text-[#1a3c4d] mb-4">
                A Gentle Touch HomeCare
              </p>
              <div className="space-y-3">
                <a 
                  href="mailto:info@agentletouchhomecarellc1.com" 
                  className="flex items-center gap-3 text-[#20a0a0] hover:text-[#1a3c4d] transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span>info@agentletouchhomecarellc1.com</span>
                </a>
                <a 
                  href="tel:614-928-4169" 
                  className="flex items-center gap-3 text-[#20a0a0] hover:text-[#1a3c4d] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>614.928.4169</span>
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
